import React, { useState } from 'react';
import Editor from '@monaco-editor/react';
import "../CodeConverter.css";
import { FaDownload, FaCompress, FaBrush, FaCopy, FaCheck, FaTrash } from "react-icons/fa";
import JsonEditorPanel from "../components/JsonEditorPanel";
import CommonSeoContent from "../components/CommonSeoContent";
import useEditorActions from "../utils/useEditorActions";

const XmlFormatter = () => {
  const [inputCode, setInputCode] = useState("");
  const [outputCode, setOutputCode] = useState("");

  // Import reusable actions
  const { handleDownload, handleClear } = useEditorActions(
    setInputCode,
    outputCode,
    setOutputCode
  );

  const formatXml = (xmlString) => {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, 'application/xml');

      const parseError = xmlDoc.getElementsByTagName('parsererror');
      if (parseError.length > 0) {
        throw new Error(parseError[0].textContent);
      }

      const serializer = new XMLSerializer();
      const prettyXml = serializer.serializeToString(xmlDoc);
      return beautifyXml(prettyXml);
    } catch (err) {
      return 'Invalid XML: ' + err.message;
    }
  };

  const beautifyXml = (xml) => {
    const PADDING = '  ';
    xml = xml.replace(/>\s*</g, '>\n<');
    const lines = xml.split('\n');
    let pad = 0;
    return lines
      .map((line) => {
        line = line.trim();
        if (line.match(/^<\/\w/)) pad -= 1;
        const indent = PADDING.repeat(pad);
        if (line.match(/^<[^!?\/][^>]*[^\/]>$/)) pad += 1;
        return indent + line;
      })
      .join('\n');
  };

  const handleFormat = () => {
    const formatted = formatXml(inputCode);
    setOutputCode(formatted);
  };

    return (
      <div className="formatter-container">
        <h1>XML Formatter</h1>

        {/* Toolbar */}
        <div className="toolbar">
          <button onClick={handleFormat} className="btn primary">
            <FaBrush /> Beautify
          </button>
          <button onClick={() => handleDownload("xml", "formatted-xml")} className="btn success">
            <FaDownload /> Download
          </button>
  		<button onClick={handleClear} className="btn danger">
            <FaTrash /> Clear
          </button>
        </div>

        {/* Editors */}
        <div className="editor-panels">
          <JsonEditorPanel title="Input XML" language="xml" code={inputCode} setCode={setInputCode} />
          <div className="editor-section-empty-div">
          </div>
          <JsonEditorPanel title="Output XML" language="xml"  code={outputCode} />
        </div>
		
		<CommonSeoContent
  title="Online XML Formatter and Validator"
  description="Format and validate XML code instantly with our free online tool. Supports indentation, syntax highlighting, and real-time validation. Safe, fast, and completely in-browser."
  keywords="XML Formatter, XML Validator, online XML tool, format XML online, validate XML, xml syntax checker, xml beautifier, xml editor, jsonconverter.org"
  author="jsonconverter.org"
  heading="Why Use XML Formatter and Validator?"
  whyUseText="Our XML Formatter and Validator tool helps you format messy or minified XML and verify its syntax correctness in real time. Whether you're debugging an API response, editing configuration files, or working with legacy XML documents, this tool gives you instant feedback with proper indentation, syntax highlighting, and error detection. Because it runs entirely in your browser, there's no need to upload your data — keeping your files private and secure. It's fast, easy to use, and built for developers, QA engineers, and data analysts."
  howToUseSteps={[
    "Paste your raw or minified XML into the input editor.",
    "Click the Format button to beautify the XML.",
    "View syntax errors (if any) highlighted automatically.",
    "Copy or download the formatted and validated XML output.",
  ]}
  features={[
    "Beautifies minified XML with proper indentation",
    "Real-time XML validation and error highlighting",
    "Supports large and deeply nested XML structures",
    "Syntax highlighting for easier debugging and editing",
    "Fully in-browser — your data stays on your device",
    "No installation or login required — always free to use",
  ]}
  inputTitle="Input XML:"
  inputExample={`<note><to>User</to><from>Admin</from><message>Hello, World!</message></note>`}
  outputTitle="Formatted XML:"
  outputExample={`<note>
  <to>User</to>
  <from>Admin</from>
  <message>Hello, World!</message>
</note>`}
  callToAction="Start formatting and validating your XML instantly — no signup or installation required."
/>

      </div>
    );
};

export default XmlFormatter;
